import { styled, Box, Button, Paper, Typography, Link as MuiLink } from '@mui/material'
import { addUsers } from 'api/management'
import csvIcon from 'assets/icons/ms-excel-icon.png'
import { useRootLoaderData } from 'components/Root.loader'
import { useAlert, useConfirm } from 'hooks/useDialog'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AddUserPayload } from 'types'

import UsersTable from './UsersImportTable'
import UploadBlock from './UsersImportUploadBlock'

// TODO: context of users2Add
export default function UserImport() {
  const [users2Add, setUsers2Add] = useState([] as AddUserPayload[])
  const alert = useAlert()
  const navigate = useNavigate()
  const doSubmit = async () => {
    try {
      const res = await addUsers(users2Add)
      if (res.status === 'NG') throw Error('my magic word')
      const someFailed = res.results?.some((r) => r.status === 'NG')
      await (someFailed
        ? alert({ message: '一部失敗しました' })
        : alert({ message: '成功しました。' }))
    } catch {
      await alert({ message: '失敗しました。' })
    } finally {
      navigate('..')
    }
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between">
        <Typography component="h3" variant="h6">
          ユーザーの一括追加
        </Typography>
        <Box display="flex">
          <Button
            component={Link}
            variant="outlined"
            color="secondary"
            to=".."
            data-testid="cancel-button"
          >
            キャンセル
          </Button>
          <SubmitButton usersCount={users2Add.length} doSubmit={doSubmit} />
        </Box>
      </Box>
      <Box maxWidth="80%" marginY={2}>
        <Description users2Add={users2Add} setUsers2Add={setUsers2Add} />
      </Box>
      <UsersTable users2Add={users2Add} setUsers2Add={setUsers2Add} />
    </Box>
  )
}

type PropTypeDesc = {
  users2Add: AddUserPayload[]
  setUsers2Add: (p: AddUserPayload[]) => void
}
function Description({ users2Add, setUsers2Add }: PropTypeDesc) {
  const rootData = useRootLoaderData()
  const templatePath = rootData.userGroupConfig.useConcur
    ? IMPORT_TEMPLATE_CONCUR_PATH
    : IMPORT_TEMPLATE_PATH

  return (
    <Paper sx={{ backgroundColor: 'grey.100' }} elevation={0}>
      <Box padding={2}>
        所定形式のCSVファイルをアップロードすることで、複数のユーザーを一括で追加できます。
        テンプレートをダウンロードし、必要項目を入力したうえでアップロードしてください。
        <Box padding={2}>
          <MuiLink
            href={templatePath}
            download
            color="primary"
            data-testid="bulk-add-users-template-csv"
          >
            <CsvIconImg alt="svg icon" src={csvIcon} />
            一括ユーザー追加テンプレートCSVファイル
          </MuiLink>
        </Box>
        <UploadBlock users2Add={users2Add} setUsers2Add={setUsers2Add} />
      </Box>
    </Paper>
  )
}

const CsvIconImg = styled('img')({
  width: '1rem',
  height: '1rem',
  display: 'inline',
})

type SubmitButtonProps = {
  usersCount: number
  doSubmit: () => Promise<void>
}
function SubmitButton({ usersCount, doSubmit }: SubmitButtonProps) {
  const confirm = useConfirm()

  const confirmMessage = (
    <>
      <p>ユーザーの一括追加を実施します</p>
      <p>
        追加されるユーザー数
        <Box component="strong" sx={{ px: 1, fontSize: '1.5rem' }}>
          {usersCount}
        </Box>
        ユーザー
      </p>
    </>
  )
  const handleClick = async () => {
    confirm({
      message: confirmMessage,
      onConfirm: () => {
        doSubmit()
      },
      actionText: '登録',
      cancelText: 'キャンセル',
    })
  }

  return (
    <Button
      onClick={handleClick}
      disabled={usersCount === 0}
      type="button"
      variant="contained"
      color="primary"
      data-testid="register-button"
    >
      登録
    </Button>
  )
}

const IMPORT_TEMPLATE_PATH = '/static/examples/users_import_template.csv'
const IMPORT_TEMPLATE_CONCUR_PATH = '/static/examples/users_import_template_concur.csv'
